import { Schema as S } from "effect";

import type { ParsedContact } from "../../import/contact";
import { defineApi } from "../client";
import {
	CommitResponse,
	ImportBatches,
	type ImportContact,
	ImportJobAccepted,
	ImportJobStatus,
	ImportJobSubmitRequest,
	ImportRow,
	ImportSource,
	MatchResponse,
	MatchRow,
	UndoPreview,
	UndoResponse,
} from "../schemas/import";

/**
 * Contact import (KTD1): match is a pure read the wizard runs before anything
 * lands; commit is submitted once as a background job and polled, while
 * undo-preview and undo are chunked, so the page drives each to completion
 * rather than the server holding a long request open.
 */

/** Drops the parser's own bookkeeping — the commit contact schema is closed. */
export const toImportContact = (contact: ParsedContact): ImportContact => ({
	name: contact.name,
	organisation: contact.organisation,
	position: contact.position,
	emails: contact.emails,
	phones: contact.phones,
	location: contact.location,
	note: contact.note,
	date: contact.date,
	labels: contact.labels,
	identity: contact.identity,
	vcardUid: contact.sourceRef,
});

/** At most `MATCH_CHUNK` rows per call. */
export const matchImport = defineApi("MatchImport", {
	args: { rows: S.Array(MatchRow) },
	response: MatchResponse,
	request: ({ rows }) => ({
		method: "POST",
		path: "/import/match",
		body: { rows },
	}),
});

/** At most `COMMIT_CHUNK` rows per call; `complete` marks the batch's last chunk. */
export const commitImportChunk = defineApi("CommitImportChunk", {
	args: {
		batchId: S.String,
		circleId: S.String,
		source: ImportSource,
		expectedRows: S.Number,
		skippedRows: S.Number,
		rows: S.Array(ImportRow),
		complete: S.Boolean,
		stopped: S.optional(S.Struct({ reason: S.String })),
	},
	response: CommitResponse,
	request: (args) => ({
		method: "POST",
		path: "/import/commit",
		// Listed field by field: `args` also carries the Command's `generation`.
		body: {
			batchId: args.batchId,
			circleId: args.circleId,
			source: args.source,
			expectedRows: args.expectedRows,
			skippedRows: args.skippedRows,
			rows: args.rows,
			complete: args.complete,
			stopped: args.stopped,
		},
	}),
});

/** Every planned row at once; the wizard then polls `pollImportJob`. Resending after a timeout is safe. */
export const submitImportJob = defineApi("SubmitImportJob", {
	args: ImportJobSubmitRequest.fields,
	response: ImportJobAccepted,
	request: (args) => ({
		method: "POST",
		path: "/import/jobs",
		// Listed field by field: `args` may also carry Command bookkeeping.
		body: {
			batchId: args.batchId,
			circleId: args.circleId,
			source: args.source,
			expectedRows: args.expectedRows,
			skippedRows: args.skippedRows,
			rows: args.rows,
		},
	}),
});

export const pollImportJob = defineApi("PollImportJob", {
	args: { batchId: S.String },
	response: ImportJobStatus,
	request: ({ batchId }) => ({
		method: "GET",
		path: `/import/jobs/${batchId}`,
	}),
});

export const listImportBatches = defineApi("ListImportBatches", {
	args: {},
	response: ImportBatches,
	request: () => ({ method: "GET", path: "/import/batches" }),
});

export const previewUndo = defineApi("PreviewUndo", {
	args: { batchId: S.String },
	response: UndoPreview,
	request: ({ batchId }) => ({
		method: "GET",
		path: `/import/batches/${batchId}/undo-preview`,
	}),
});

/** Re-post the returned `cursor` until `done`; a second undo of a done batch is a no-op. */
export const undoImportBatch = defineApi("UndoImportBatch", {
	args: { batchId: S.String, cursor: S.NullOr(S.Number) },
	response: UndoResponse,
	request: ({ batchId, cursor }) => ({
		method: "POST",
		path: `/import/batches/${batchId}/undo`,
		body: cursor === null ? {} : { cursor },
	}),
});
